import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import i18n from "../i18n";
import Utils from "../utils/Utils";
const { serverURL } = Utils;

const initialState = {
  status: "idle",
  managingEventStatus: "idle",
  managingEvent: null,
  invitationRequests: [],
  requestsStatus: "idle",
  totalRequests: 0,
};

export const editAutoAccept = createAsyncThunk(
  "event/editAutoAccept",
  async (data, { rejectWithValue }) => {
    try {
      const response = await fetch(`${serverURL}/event/edit-auto-accept`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || i18n.t("updateFailed"));
      }

      return await response.json();
    } catch (error) {
      toast.error(`${i18n.t("updateFailed")} ${error.message}`);
      return rejectWithValue(error.message);
    }
  }
);

export const getEventById = createAsyncThunk(
  "event/getEventById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch(`${serverURL}/event/get`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || i18n.t("failedToFetchEvent"));
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchInvitationRequests = createAsyncThunk(
  "event/fetchInvitationRequests",
  async (
    { eventId, page, limit, sortField, sortOrder },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams({
        page: page,
        limit,
        sortField: sortField,
        order: sortOrder,
      });
      const response = await fetch(
        `${serverURL}/event/${eventId}/requests?${params}`,
        {
          credentials: "include",
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || i18n.t("failedToFetchInvitationRequests")
        );
      }
      return await response.json();
    } catch (error) {
      toast.error(`${i18n.t("errorFetchingRequests")} ${error.message}`);
      return rejectWithValue(error.message);
    }
  }
);

export const updateInvitationStatus = createAsyncThunk(
  "event/updateInvitationStatus",
  async ({ requestId, status }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${serverURL}/event/requests/${requestId}/status`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || i18n.t("errorUpdatingRequest"));
      }

      const updatedRequest = await response.json();
      if (status === "accepted") {
        toast.success(i18n.t("requestAccepted"));
      } else if (status === "declined") {
        toast.success(i18n.t("requestDeclined"));
      }
      return updatedRequest;
    } catch (error) {
      toast.error(`${i18n.t("errorUpdatingRequestPrefix")} ${error.message}`);
      return rejectWithValue(error.message);
    }
  }
);

const eventManagementSlice = createSlice({
  name: "eventManagement",
  initialState,
  reducers: {
    clearInvitationRequests: (state) => {
      state.invitationRequests = [];
      state.requestsStatus = "idle";
      state.totalRequests = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(editAutoAccept.fulfilled, (state, action) => {
        state.managingEvent.autoAcceptInvitationRequests =
          action.payload.autoAccept;
        toast.success(i18n.t("updateSuccessful"));
      })
      .addCase(editAutoAccept.rejected, (state, action) => {
        toast.error(`${i18n.t("updateFailed")} ${action.payload}`);
      })
      .addCase(getEventById.pending, (state) => {
        state.managingEventStatus = "loading";
      })
      .addCase(getEventById.fulfilled, (state, action) => {
        state.managingEvent = action.payload;
        state.managingEventStatus = "success";
      })
      .addCase(getEventById.rejected, (state, action) => {
        state.managingEventStatus = "failed";
        toast.error(`${i18n.t("errorFetchingEventPrefix")} ${action.payload}`);
      })
      .addCase(fetchInvitationRequests.pending, (state) => {
        state.requestsStatus = "loading";
      })
      .addCase(fetchInvitationRequests.fulfilled, (state, action) => {
        state.requestsStatus = "success";
        state.invitationRequests = action.payload.requests;
        state.totalRequests = action.payload.total;
      })
      .addCase(fetchInvitationRequests.rejected, (state, action) => {
        state.requestsStatus = "failed";
      })
      .addCase(updateInvitationStatus.fulfilled, (state, action) => {
        const updatedRequest = action.payload;
        const index = state.invitationRequests.findIndex(
          (req) => req._id === updatedRequest._id
        );
        if (index !== -1) {
          state.invitationRequests[index] = updatedRequest;
        }
      })
      .addCase(updateInvitationStatus.rejected, (state, action) => {
        console.error("Failed to update invitation status:", action.payload);
      });
  },
});

export const { clearInvitationRequests } = eventManagementSlice.actions;

export default eventManagementSlice.reducer;
